import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import { STATUS_CONFIG } from '../constants';
import {
  C, FormShell, Field, TextInput, Select, TextArea, SubmitButton, Banner,
} from './FormKit';

const VEHICLE_TYPES = ['22WHL', '18WHL', '16WHL', '14WHL', '12WHL', '10WHL', '06WHL', 'TRACTOR', 'Others'];
const LOAD_TYPES = ['10mm', '20mm', '30mm', '40mm', '60mm', 'DUST', 'BOULDER', 'WMM', 'GSB', 'BC-1-Dust', 'OTHER'];
const REASONS = [
  { code: '', label: '— select a reason —' },
  { code: 'WRONG_PLATE', label: 'Wrong number plate entered' },
  { code: 'WRONG_VEHICLE_TYPE', label: 'Wrong vehicle type' },
  { code: 'WRONG_LOAD', label: 'Wrong expected load' },
  { code: 'TYPO', label: 'Typo / correction' },
  { code: 'OTHER', label: 'Other (explain below)' },
];

function fmtDuration(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function UpdateForm() {
  const [vehicles, setVehicles] = useState([]);
  const [selTx, setSelTx]       = useState('');

  // search dropdown
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // editable fields
  const [plate, setPlate]   = useState('');
  const [vType, setVType]   = useState('TRUCK');
  const [eLoad, setELoad]   = useState('');
  const [notes, setNotes]   = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');

  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  const selected = vehicles.find(v => v.transactionId === selTx);

  const loadVehicles = useCallback(async () => {
    try { setVehicles(await gateApi.getInProgressVehicles()); }
    catch { /* keep last list */ }
  }, []);

  useEffect(() => {
    loadVehicles();
    const v = setInterval(loadVehicles, 15000);
    return () => clearInterval(v);
  }, [loadVehicles]);

  // close dropdown on outside click
  useEffect(() => {
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate);
    setShowDropdown(false);
    // pre-fill editable fields from the current record
    setPlate(v.licensePlate || '');
    setVType((v.vehicleType || 'TRUCK').toUpperCase());
    setELoad(v.expectedLoad || '');
    setNotes(v.operatorNotes || '');
    setReasonCode(''); setReasonText('');
    setResult(null);
  }

  function clearSelection() {
    setSelTx(''); setSearchQuery('');
    setPlate(''); setVType('TRUCK'); setELoad(''); setNotes('');
    setReasonCode(''); setReasonText('');
  }

  const plateChanged = selected && plate.toUpperCase().trim() !== (selected.licensePlate || '').toUpperCase().trim();

  async function submit() {
    if (!selTx) { setResult({ ok: false, msg: 'Select a vehicle first.' }); return; }
    if (!reasonCode) { setResult({ ok: false, msg: 'Pick a reason for the change.' }); return; }
    if (reasonCode === 'OTHER' && !reasonText.trim()) {
      setResult({ ok: false, msg: 'Explain the reason in the text box.' }); return;
    }
    setBusy(true); setResult(null);
    try {
      const r = await gateApi.updateTransaction(selTx, {
        licensePlate: plate,
        vehicleType: vType,
        expectedLoad: eLoad,
        operatorNotes: notes,
        reasonCode, reasonText,
      });
      if (r.status === 'UPDATED') {
        const changed = (r.changes || []).map(c => c.field).join(', ') || 'nothing';
        setResult({
          ok: true,
          msg: `Updated: ${changed}.` + (r.flagged ? ' Plate change sent for admin review.' : ''),
        });
        clearSelection();
        loadVehicles();
      } else {
        setResult({ ok: false, msg: 'No changes detected.' });
      }
    } catch (e) {
      const d = e.response?.data;
      if (d?.error === 'DUPLICATE') {
        setResult({ ok: false, msg: d.message || 'Another active vehicle already has that plate.' });
      } else if (d?.error === 'no changes detected') {
        setResult({ ok: false, msg: 'You did not change anything.' });
      } else {
        setResult({ ok: false, msg: d?.message || d?.error || 'Could not save the change.' });
      }
    } finally {
      setBusy(false);
    }
  }

  let minsInside = selected?.minutesInside;
  if (selected?.entryTime) {
    minsInside = Math.max(0, Math.round((Date.now() - new Date(selected.entryTime)) / 60000));
  }

  return (
    <FormShell title="Update" accent={C.amber}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Correct a vehicle</h1>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 26px' }}>
        Fix details for a vehicle currently inside the site. Every change is logged with a reason.
      </p>

      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      {/* Search in-progress vehicles — any stage, not just one status */}
      <Field label="Find vehicle (inside now)"
             hint={vehicles.length ? `${vehicles.length} vehicle(s) inside, any stage` : 'No vehicles currently inside'}>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder={vehicles.length ? 'Type to search number plate…' : 'none inside'}
            disabled={!vehicles.length}
            value={searchQuery}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => { setSearchQuery(e.target.value); setSelTx(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.panel2,
              border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
              padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          />
          {showDropdown && vehicles.length > 0 && (
            <div style={{
              position: 'absolute', top: '102%', left: 0, right: 0, background: C.panel,
              border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: 220,
              overflowY: 'auto', zIndex: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '6px 0',
            }}>
              {filtered.length > 0 ? filtered.map(v => {
                // displayStatus splits "billed, unpaid" out of PENDING_CHALLAN —
                // both share that lifecycleStatus, so the raw value alone always
                // reads "Awaiting Challan" (see /transactions/in-progress).
                const stage = STATUS_CONFIG[v.displayStatus || v.lifecycleStatus];
                return (
                  <div key={v.transactionId} onClick={() => selectVehicle(v)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = C.panel2}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                      <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.text }}>{v.licensePlate}</span>
                      <span style={{ color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                        {v.vehicleType || 'VEHICLE'} · {fmtDuration(v.minutesInside)} inside
                      </span>
                    </span>
                    {stage && (
                      <span style={{
                        flexShrink: 0, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                        padding: '3px 9px', borderRadius: 20, letterSpacing: '0.03em',
                        background: stage.bg, color: stage.color,
                      }}>{stage.label}</span>
                    )}
                  </div>
                );
              }) : (
                <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13 }}>
                  No matching vehicles inside
                </div>
              )}
            </div>
          )}
        </div>
      </Field>

      {/* Edit card — only once a vehicle is selected */}
      {selected && (
        <>
          <div style={{
            background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: '12px 16px', marginBottom: 22, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13,
          }}>
            <span style={{ color: C.muted }}>Entered:&nbsp;
              <span style={{ color: C.text, fontFamily: C.mono }}>
                {selected.entryTime ? new Date(selected.entryTime).toLocaleTimeString() : '—'}</span></span>
            <span style={{ color: C.muted }}>Time inside:&nbsp;
              <span style={{ color: C.amber, fontFamily: C.mono }}>{fmtDuration(minsInside)}</span></span>
          </div>

          <Field label="Number plate">
            <TextInput value={plate} onChange={setPlate} mono
              style={{ fontWeight: 700, textTransform: 'uppercase' }} />
          </Field>
          {plateChanged && (
            <div style={{ marginTop: -10, marginBottom: 18, fontSize: 13, color: C.amber }}>
              ⚑ Changing the plate will be flagged for admin review.
            </div>
          )}

          <Field label="Vehicle type">
            <Select value={vType} onChange={setVType} options={VEHICLE_TYPES} />
          </Field>

          <Field label="Expected load">
            <Select value={eLoad || ''} onChange={setELoad}
              options={[{ value: '', label: '—' }, ...LOAD_TYPES.map(l => ({ value: l, label: l }))]} />
          </Field>

          <Field label="Notes">
            <TextArea value={notes} onChange={setNotes} placeholder="Remarks…" />
          </Field>

          <div style={{ height: 1, background: C.border, margin: '6px 0 20px' }} />

          <Field label="Reason for change (required)">
            <Select value={reasonCode} onChange={setReasonCode}
              options={REASONS.map(r => ({ value: r.code, label: r.label }))} />
          </Field>

          {reasonCode === 'OTHER' && (
            <Field label="Explain">
              <TextArea value={reasonText} onChange={setReasonText} placeholder="What was wrong and why…" />
            </Field>
          )}

          <SubmitButton onClick={submit} disabled={busy} color={C.amber}>
            {busy ? 'Saving…' : 'Save correction'}
          </SubmitButton>

          <button onClick={clearSelection} style={{
            marginTop: 14, width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer', letterSpacing: '0.08em',
          }}>CANCEL</button>
        </>
      )}
    </FormShell>
  );
}